// Library methods
import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// MUI Components
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";

// Components
import Alert from "../../../components/UI/Alert";
import AddExamPackageSlider from "./AddExamPackageSlider";
import DeleteExamPackageModal from "./DeleteExamPackageModal";
import EditExamPackageSlider from "./EditExamPackageSlider";
import { EditIcon, PlusIcon, TrashIcon } from "../../../assets/icons";

// Styles
import {
  CustomIconButtonDark,
  ModalCustomCancel,
  ModalCustomConfirmDark,
} from "../../../styles/muiStylesHelper";

// Utils
import {
  getPredefinedExams,
  addPredefinedExam,
  deletePredefinedExam,
  updatePredefinedExam,
} from "../../../services/Clinic";
import useClinic from "../../../hooks/useClinic";
import { getEyeText, getTooltipText } from "../../../utils/examReportsHelper";
import { useAddExams } from "../../../hooks/exams/useAddExams";
import { useExamListContext } from "../../../contexts/ExamListContext";
import { useAddEditExamHelpers } from "../../../hooks/exams/useAddEditExamHelpers";
import { useEditExams } from "../../../hooks/exams/useEditExams";
import { useComponentDimensionsById } from "../../../hooks/useComponentDimensionsById";

const PredefinedExamSettings = () => {
  const { t } = useTranslation();

  // auth0
  const { getAccessTokenSilently } = useAuth0();

  // clinic context
  const { clinicId, clinicLicensing } = useClinic();
  const canUseExamPackaging = clinicLicensing?.canUseExamPackaging;

  const { dynamicWidth } = useComponentDimensionsById(null, "#package-header");

  const { examCards, sectionTypeOrder, actions } = useExamListContext();
  const { setExamCards } = actions;
  const { defaultExamOrder, getDeselectedExamOrder, generateExamsToEdit } =
    useAddEditExamHelpers();

  const { getFormattedExamSections: formatExamToAdd } = useAddExams();
  const { getFormattedExamSections: formatExamToEdit } = useEditExams();

  const [isLoading, setIsLoading] = useState(false);
  const [predefinedExams, setPredefinedExams] = useState([]);

  // values to display on modals / drawers
  const [packageName, setPackageName] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(undefined);

  // open / close modal and drawer values
  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [openEditDrawer, setOpenEditDrawer] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const resetValues = useCallback(() => {
    setExamCards({ selected: [], deselected: [...defaultExamOrder] });
    setPackageName("");
    setSelectedPackage(undefined);
  }, [defaultExamOrder, setExamCards]);

  const onCloseAddDrawer = useCallback(() => {
    setOpenAddDrawer(false);
    resetValues();
  }, [setOpenAddDrawer, resetValues]);

  const onCloseEditDrawer = useCallback(() => {
    setOpenEditDrawer(false);
    resetValues();
  }, [setOpenEditDrawer, resetValues]);

  const fetchClinicPackages = useCallback(async () => {
    try {
      // get authToken
      const token = await getAccessTokenSilently();
      const data = await getPredefinedExams(token, clinicId);
      setPredefinedExams([...data]);
    } catch (e) {
      console.log(e);
    }
  }, [clinicId, getAccessTokenSilently]);

  // initial data fetching
  useEffect(() => {
    fetchClinicPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddExamPackage = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const examData = {
        name: packageName,
        content: JSON.stringify({
          sectionTypeOrder,
          ...formatExamToAdd({ selectedExamCards: examCards.selected }),
        }),
      };
      await addPredefinedExam(token, clinicId, examData);
      await fetchClinicPackages();
      setOpenAddDrawer(false);
      resetValues();
      setIsLoading(false);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    clinicId,
    packageName,
    getAccessTokenSilently,
    formatExamToAdd,
    fetchClinicPackages,
    resetValues,
    examCards.selected,
    sectionTypeOrder,
  ]);

  const onUpdateExamPackage = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const examData = {
        name: packageName,
        content: JSON.stringify({
          sectionTypeOrder,
          ...formatExamToEdit({ selectedExamCards: examCards.selected }),
        }),
      };
      await updatePredefinedExam(
        token,
        clinicId,
        selectedPackage?.id,
        examData
      );
      await fetchClinicPackages();
      setOpenEditDrawer(false);
      resetValues();
      setIsLoading(false);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    clinicId,
    packageName,
    selectedPackage,
    getAccessTokenSilently,
    formatExamToEdit,
    fetchClinicPackages,
    resetValues,
    examCards.selected,
    sectionTypeOrder,
  ]);

  const onDeleteExamPackage = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await deletePredefinedExam(token, clinicId, selectedPackage?.id);
      await fetchClinicPackages();
      setOpenDeleteModal(false);
      resetValues();
    } catch (e) {
      console.log(e);
    }
  }, [
    clinicId,
    selectedPackage,
    getAccessTokenSilently,
    fetchClinicPackages,
    resetValues,
  ]);

  const getFormattedContent = useCallback(
    (predefinedExam) => {
      const packageContent = JSON.parse(predefinedExam?.content);
      const packageDescription = getTooltipText(packageContent, t)
        ?.split("\n")
        ?.filter((content) => Boolean(content.length));
      const eyeDescription = getEyeText(packageContent, t)
        ?.split("\n")
        ?.filter((content) => Boolean(content.length));

      const contentArray = packageDescription.map((description, index) => ({
        packageDescription: description,
        eyeDescription: eyeDescription[index],
      }));

      return contentArray;
    },
    [t]
  );

  // don't allow editing multi-exam packages if multi-exam licensing is turned off
  const hideEditButton = useCallback(
    (packageContent) => {
      return (
        !canUseExamPackaging &&
        JSON.parse(packageContent)?.sectionTypeOrder?.length > 1
      );
    },
    [canUseExamPackaging]
  );

  // format the edit drawer values after clicking the edit button
  const onEditClick = useCallback(
    (predefinedExam) => {
      const examContent = JSON.parse(predefinedExam?.content);
      const deselectedExamCards = getDeselectedExamOrder({ exam: examContent });
      const selectedExamCards = generateExamsToEdit({
        exam: examContent,
      });
      setExamCards({
        selected: [...selectedExamCards],
        deselected: [...deselectedExamCards],
      });
      setSelectedPackage(predefinedExam);
      setPackageName(predefinedExam?.name);
      setOpenEditDrawer(true);
    },
    [generateExamsToEdit, getDeselectedExamOrder, setExamCards]
  );

  return (
    <>
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography fontSize={{ xs: "18px", sm: "20px" }} fontWeight={600}>
          {t("exam_package_title")}
        </Typography>
        <Box display="flex" flexDirection="column" gap="20px">
          <Typography fontSize="14px" color="#5B6D7A">
            {t("create_exam_package_text")}
          </Typography>
          {predefinedExams?.length === 0 && (
            <Alert
              variant="warning"
              alertContent={t("no_exam_packages_text")}
            />
          )}
          {predefinedExams
            ?.sort((a, b) => a?.name?.localeCompare(b?.name))
            ?.map((predefinedExam) => (
              <Box
                key={predefinedExam?.id}
                display="flex"
                flexDirection="column"
                gap="20px"
              >
                <Box
                  id="package-header"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography
                    fontSize={{ xs: "16px", sm: "18px" }}
                    fontWeight={600}
                    sx={{
                      maxWidth: `calc(${dynamicWidth}px - 225px)`,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {predefinedExam?.name}
                  </Typography>
                  <Box display="flex" gap="8px" sx={{ maxWidth: "152px" }}>
                    <Button
                      onClick={() => onEditClick(predefinedExam)}
                      sx={{
                        ...ModalCustomCancel(),
                        display: hideEditButton(predefinedExam?.content)
                          ? "none"
                          : "flex",
                      }}
                    >
                      <Box display="flex" gap="8px">
                        <EditIcon />
                        <Typography>{t("word_edit")}</Typography>
                      </Box>
                    </Button>
                    <Tooltip title={t("delete_exam_package")}>
                      <IconButton
                        data-testid="delete-icon"
                        sx={CustomIconButtonDark}
                        onClick={() => {
                          setSelectedPackage(predefinedExam);
                          setOpenDeleteModal(true);
                        }}
                      >
                        <TrashIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  backgroundColor="#5B6D7A10"
                  borderRadius="8px"
                  padding="20px"
                  gap="8px"
                >
                  {getFormattedContent(predefinedExam)?.map(
                    (content, index) => (
                      <Box
                        key={content.packageDescription}
                        display="flex"
                        justifyContent="space-between"
                      >
                        <Typography fontSize={{ xs: "14px", sm: "16px" }}>
                          {index + 1}. {content.packageDescription}
                        </Typography>
                        <Typography
                          fontSize={{ xs: "14px", sm: "16px" }}
                          textAlign="right"
                        >
                          {content.eyeDescription}
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
              </Box>
            ))}

          {predefinedExams?.length < 3 && (
            <Button
              sx={ModalCustomConfirmDark}
              onClick={() => setOpenAddDrawer(true)}
            >
              <Box display="flex" gap="8px">
                <PlusIcon />
                <Typography>{t("add_exam_package")}</Typography>
              </Box>
            </Button>
          )}
        </Box>
      </Box>

      <DeleteExamPackageModal
        open={openDeleteModal}
        descriptionKey={t("exam_package_delete_description", {
          packageName: selectedPackage?.name,
        })}
        onCancel={() => {
          setOpenDeleteModal(false);
          resetValues();
        }}
        onClose={() => {
          setOpenDeleteModal(false);
          resetValues();
        }}
        onConfirm={onDeleteExamPackage}
      />
      <AddExamPackageSlider
        isLoading={isLoading}
        visible={openAddDrawer}
        onClose={onCloseAddDrawer}
        onConfirm={onAddExamPackage}
        packageName={packageName}
        setPackageName={setPackageName}
      />
      <EditExamPackageSlider
        isLoading={isLoading}
        visible={openEditDrawer}
        onClose={onCloseEditDrawer}
        onConfirm={onUpdateExamPackage}
        packageName={packageName}
        setPackageName={setPackageName}
      />
    </>
  );
};

export default PredefinedExamSettings;
